import { FC, useEffect, useRef, useState } from 'react';
import { ReactComponent as CalendarSvg } from 'assets/img/filter/iconCalendarBig.svg';
import { DatePickerChoose } from 'components/DatePicker';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';

import {
  currentYearNewFormat,
  formattedDateMozilla,
  getDateFormattedDot,
} from 'utils/date';
import classNames from 'classnames/bind';
import styles from './InputDate.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  setErrorStatusField,
  setNotErrorStatusField,
  setWasChanges,
} from 'redux/mainInfo/slice';
import { RootState } from 'redux/store';

const cx = classNames.bind(styles);

type InputDateProps = {
  placeholder: string;
  date: Date | string;
  setDate: ActionCreatorWithPayload<Date | null | string>;
  error?: boolean;
  nameError?: string;
  withoutHour?: boolean;
  draft?: boolean;
  style?: React.CSSProperties;
};

export const InputDate: FC<InputDateProps> = ({
  placeholder,
  date,
  setDate,
  error,
  nameError,
  withoutHour = true,
  style,
}) => {
  const dateRef = useRef<HTMLDivElement>(null);
  const { isModerated } = useSelector((state: RootState) => state.mainInfo);
  const [value, setValue] = useState<string>(typeof date === 'string' ? date : '');
  const [openDate, setOpenDate] = useState<boolean>(false);
  const dispatch = useDispatch();
  const classNameInput = cx({
    input: true,
    input_value: value,
    inputError: error,
  });

  const classNamePlaceholder = cx({
    placeholder: true,
    inputPlaceholderError: error,
  });

  const classNameCalendar = cx({
    calendar: true,
    inputCalendarError: error,
    isModerated: isModerated,
  });

  const openCalendar = (e: React.MouseEvent<HTMLDivElement>) => {
    dispatch(setDate(''));
    e.stopPropagation();
    setOpenDate(!openDate);
    if (error) dispatch(setNotErrorStatusField(nameError));
  };

  const changeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setErrorStatusField({ key: nameError, value: 0 }));
    dispatch(setWasChanges(true));
    const regExp = /[^0-9-.:\s]/g;
    const value = e.target.value.replace(regExp, '');

    if (value.length < 10) {
      dispatch(setErrorStatusField({ key: nameError, value: 1 }));
    }
    if (value.length >= 10 && withoutHour) {
      if (+value.slice(6) > +currentYearNewFormat) {
        dispatch(setErrorStatusField(nameError));
      } else {
        dispatch(setNotErrorStatusField(nameError));
      }
      const parts = e.target.value.split('.');
      dispatch(setDate(new Date(formattedDateMozilla(parts))));
      +value.slice(6, 11) > +currentYearNewFormat;
    }

    if (typeof date === 'object' && date instanceof Date && isNaN(date.getTime())) {
      setValue('');
    } else if (typeof date === 'string' && isNaN(Number(date))) {
      setValue('');
    }

    if (value?.length === 2) {
      setValue(`${value}.`);
    } else if (value?.length === 5) {
      setValue(`${value}.`);
    } else if (value?.length === 10 && !withoutHour) {
      setValue(`${value} `);
    } else if (value?.length === 13 && !withoutHour) {
      setValue(`${value}:`);
    } else setValue(value);
  };

  const keyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    dispatch(setWasChanges(true));

    if (e.key === 'Backspace') setValue('');
  };

  useEffect(() => {
    const clickOutside = (event: MouseEvent) => {
      if (dateRef.current && !event.composedPath().includes(dateRef.current)) {
        setOpenDate(false);
      }
    };
    document.body.addEventListener('click', clickOutside);
    return () => document.body.removeEventListener('click', clickOutside);
  }, []);

  useEffect(() => {
    if (typeof date === 'object' && date instanceof Date && !isNaN(date.getTime())) {
      setValue(getDateFormattedDot(date));
    } else if (typeof date === 'string') {
      setValue(date);
    }
  }, [date]);

  const onBlur = () => {
    if (!value && nameError === 'doc_date') {
      dispatch(setErrorStatusField({ key: nameError, value: 1 }));
    }
  };

  return (
    <div className={styles.root}>
      <span
        style={isModerated ? { background: 'transparent' } : {}}
        className={classNamePlaceholder}>
        {placeholder}
      </span>

      <input
        style={style}
        disabled={isModerated}
        className={classNameInput}
        type='text'
        onChange={changeDate}
        onKeyDown={keyDown}
        value={value}
        placeholder='дд.мм.гггг'
        maxLength={withoutHour ? 10 : 16}
        onBlur={onBlur}
      />
      <div className={classNameCalendar} onClick={isModerated ? undefined : openCalendar}>
        <CalendarSvg />
      </div>

      {openDate && (
        <div className={styles.datePicker} ref={dateRef}>
          <DatePickerChoose
            withoutHour={true}
            setOpen={setOpenDate}
            setDate={setDate}
            date={typeof date === 'string' ? null : date}
            nameError={nameError}
          />
        </div>
      )}
    </div>
  );
};
